import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { useAuth } from "@/context/AuthContext";
import { SectionTitle } from "@/components/SectionTitle";
import { BoxScoreModal } from "@/components/BoxScoreModal";
import { CalendarBlank, MapPin, Basketball, ClipboardText, Clock, ListBullets, Printer, Trophy } from "@phosphor-icons/react";
import { PlayoffScheduleTable } from "@/components/PlayoffScheduleTable";

const STATUS = {
  scheduled: { label: "Upcoming", cls: "bg-[#FF5722]/20 text-[#FF5722]" },
  live: { label: "● Live", cls: "bg-[#00E676]/20 text-[#00E676]" },
  completed: { label: "Final", cls: "bg-white/10 text-white/70" },
  postponed: { label: "Postponed", cls: "bg-[#FFB300]/20 text-[#FFB300]" },
  cancelled: { label: "Cancelled", cls: "bg-[#FF3B30]/20 text-[#FF3B30]" },
};

function Logo({ team }) {
  if (!team) return null;
  return (
    <span className="w-9 h-9 sm:w-11 sm:h-11 flex items-center justify-center text-black font-display overflow-hidden shrink-0" style={{ background: team.primary_color || "#FF5722" }}>
      {team.logo_url ? <img src={team.logo_url} alt={team.name} className="w-full h-full object-cover" /> : team.name.charAt(0)}
    </span>
  );
}

export default function Schedule() {
  const { settings } = useSettings();
  const { admin } = useAuth();
  const DIVISIONS = useMemo(() => settings.divisions || [], [settings.divisions]);
  const [games, setGames] = useState([]);
  const [teams, setTeams] = useState([]);
  const [weeks, setWeeks] = useState([]);
  const [weeksByDiv, setWeeksByDiv] = useState({});
  const [filter, setFilter] = useState("all");
  const [teamFilter, setTeamFilter] = useState("all");
  const [division, setDivision] = useState("Open");
  const [allMode, setAllMode] = useState(false);
  const [playoffMode, setPlayoffMode] = useState(false);
  const [playoffData, setPlayoffData] = useState(null);
  const [boxGame, setBoxGame] = useState(null);

  useEffect(() => {
    api.get("/games").then((r) => setGames(r.data)).catch(() => {});
    api.get("/teams").then((r) => setTeams(r.data)).catch(() => {});
    api.get("/playoffs").then((r) => setPlayoffData(r.data)).catch(() => {});
  }, []);

  // Load week metadata for every division (used by All-Division view to honor published weeks).
  useEffect(() => {
    if (!DIVISIONS.length) return;
    Promise.all(DIVISIONS.map((d) => api.get(`/weeks?division=${encodeURIComponent(d)}`).then((r) => [d, r.data]).catch(() => [d, []])))
      .then((pairs) => setWeeksByDiv(Object.fromEntries(pairs)));
  }, [DIVISIONS]);

  useEffect(() => {
    if (!division) return;
    api.get(`/weeks?division=${encodeURIComponent(division)}`).then((r) => setWeeks(r.data)).catch(() => setWeeks([]));
    setTeamFilter("all");
  }, [division]);

  useEffect(() => {
    if (DIVISIONS.length && !DIVISIONS.includes(division)) setDivision(DIVISIONS[0]);
  }, [DIVISIONS, division]);

  const teamName = (id) => teams.find((t) => t.id === id)?.name || "TBD";
  const teamOf = (id) => teams.find((t) => t.id === id);
  const divisionOf = (id) => teams.find((t) => t.id === id)?.division || "Open";
  const divTeams = teams.filter((t) => (t.division || "Open") === division);

  const hasWeeks = weeks.length > 0;
  const publishedNums = new Set(weeks.filter((w) => w.published).map((w) => w.number));
  const weekMeta = (n) => weeks.find((w) => w.number === n);

  const matchFilters = (g) => {
    // Main view (filter = "all") hides completed games; they remain accessible via the "Final" filter.
    if (filter === "all") { if (g.status === "completed") return false; }
    else if (g.status !== filter) return false;
    if (teamFilter !== "all" && g.home_team_id !== teamFilter && g.away_team_id !== teamFilter) return false;
    return true;
  };

  const divGames = games.filter((g) => !g.is_playoff && divisionOf(g.home_team_id) === division && matchFilters(g));
  const grpDate = (grp) => grp.games[0]?.date || "";

  // Build groups: if the division has weeks, show only published weeks; else show everything under one group.
  // Weeks are ordered chronologically by their earliest game so the current/upcoming week sits at the top,
  // and fully-completed weeks drop out of the main view automatically (their games are hidden).
  const groups = useMemo(() => {
    if (hasWeeks) {
      return [...publishedNums].sort((a, b) => a - b).map((n) => ({
        key: `w${n}`, week: n, meta: weekMeta(n),
        games: divGames.filter((g) => g.week === n).sort((a, b) => a.date.localeCompare(b.date) || (a.order || 0) - (b.order || 0)),
      })).filter((grp) => grp.games.length > 0).sort((a, b) => grpDate(a).localeCompare(grpDate(b)));
    }
    return [{ key: "all", week: null, meta: null, games: [...divGames].sort((a, b) => a.date.localeCompare(b.date)) }];
  }, [divGames, hasWeeks]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalShown = groups.reduce((n, g) => n + g.games.length, 0);

  // ---- All-Division view: every division's published games, grouped by week, sorted date/time/court ----
  const statusOk = (g) => (filter === "all" ? g.status !== "completed" : g.status === filter);
  const isPublishedGame = (g) => {
    const div = divisionOf(g.home_team_id);
    const wks = weeksByDiv[div] || [];
    if (wks.length === 0) return true;
    const w = wks.find((x) => x.number === g.week);
    return w ? !!w.published : false;
  };
  const sortGames = (arr) => [...arr].sort((a, b) =>
    a.date.localeCompare(b.date) || (a.court || "").localeCompare(b.court || "") || (a.order || 0) - (b.order || 0));

  const allVisible = useMemo(
    () => games.filter((g) => !g.is_playoff && isPublishedGame(g) && statusOk(g)),
    [games, weeksByDiv, filter] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const allGroups = useMemo(() => {
    const byWeek = {};
    allVisible.forEach((g) => { const k = g.week ?? 0; (byWeek[k] = byWeek[k] || []).push(g); });
    return Object.keys(byWeek).map(Number).map((n) => ({ week: n, games: sortGames(byWeek[n]) }))
      .sort((a, b) => (a.games[0]?.date || "").localeCompare(b.games[0]?.date || ""));
  }, [allVisible]); // eslint-disable-line react-hooks/exhaustive-deps

  const fmtDate = (d) => new Date(d).toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });
  const fmtTime = (d) => new Date(d).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  const ROUND_CODE = { "Round of 16": "R16", "Quarterfinals": "QF", "Semifinals": "SF", "Championship": "F" };

  const printSchedule = (divisionName = null, playoffsOnly = false) => {
    const season = settings.hero?.season_title || "";
    const logo = `${window.location.origin}/iefsc-logo.png`;
    const esc = (s) => String(s ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
    const statusLabel = (g) => (STATUS[g.status] || STATUS.scheduled).label.replace("● ", "");
    const rowHtml = (g) => `<tr>
      <td>${esc(fmtDate(g.date))}</td><td>${esc(fmtTime(g.date))}</td><td>${esc(g.court || "—")}</td>
      <td>${esc(teamName(g.home_team_id))} <span class="vs">vs</span> ${esc(teamName(g.away_team_id))}</td>
      <td class="st">${esc(statusLabel(g))}</td></tr>`;
    const head = `<thead><tr><th>Date</th><th>Time</th><th>Court</th><th>Matchup</th><th>Status</th></tr></thead>`;

    // ---- Playoff rounds (per division bracket) ----
    const pTeam = (bt, tid) => { const t = tid ? bt[tid] : null; return t ? `<span class="seed">#${esc(t.seed)}</span> ${esc(t.name)}` : `<span class="tbd">TBD</span>`; };
    const pStatus = (m) => {
      if (m.status === "completed") return `<b>${m.a_score}–${m.b_score}</b>`;
      if (!(m.a_team_id && m.b_team_id)) return "Awaiting teams";
      return m.date ? "Scheduled" : "TBA";
    };
    const pHead = `<thead><tr><th>Game</th><th>Date</th><th>Time</th><th>Court</th><th>Matchup</th><th>Score / Status</th></tr></thead>`;
    const playoffHtml = (divKeys) => (playoffData?.divisions || []).filter((d) => !divKeys || divKeys.includes(d.key)).map((d) => {
      const b = playoffData.brackets[d.key];
      if (!b?.rounds) return "";
      const bt = b.teams || {};
      const rounds = b.rounds.map((rd) => {
        const ms = rd.matches.filter((m) => m.status !== "bye");
        if (!ms.length) return "";
        const code = ROUND_CODE[rd.name] || rd.name.slice(0, 2).toUpperCase();
        const rows = ms.map((m) => `<tr>
          <td class="code">${esc(code)}${m.pos + 1}</td><td>${m.date ? esc(fmtDate(m.date)) : "—"}</td><td>${m.date ? esc(fmtTime(m.date)) : "—"}</td>
          <td>${esc(m.court || "—")}</td><td>${pTeam(bt, m.a_team_id)} <span class="vs">vs</span> ${pTeam(bt, m.b_team_id)}</td>
          <td class="st">${pStatus(m)}</td></tr>`).join("");
        return `<h3>${esc(rd.name)}</h3><table>${pHead}<tbody>${rows}</tbody></table>`;
      }).join("");
      const champ = b.champion_team_id && bt[b.champion_team_id] ? `<p class="champ">Champion: ${esc(bt[b.champion_team_id].name)}</p>` : "";
      return `<section class="wk po"><h2>${esc(d.label)} Division — Playoffs</h2>${rounds}${champ}</section>`;
    }).join("");

    const src = divisionName ? groups : allGroups;
    const title = playoffsOnly ? "Playoff Schedule" : divisionName ? `${divisionName} Division — Schedule` : "Game Schedule";
    const regular = playoffsOnly ? "" : src.map((grp) => {
      if (divisionName) {
        return `<section class="wk"><h2>Week ${grp.week || ""}</h2><table>${head}<tbody>${grp.games.map(rowHtml).join("")}</tbody></table></section>`;
      }
      const byDiv = {};
      grp.games.forEach((g) => { const d = divisionOf(g.home_team_id); (byDiv[d] = byDiv[d] || []).push(g); });
      const divSections = Object.keys(byDiv).sort().map((d) =>
        `<h3>${esc(d)} Division</h3><table>${head}<tbody>${byDiv[d].map(rowHtml).join("")}</tbody></table>`).join("");
      return `<section class="wk"><h2>Week ${grp.week || ""}</h2>${divSections}</section>`;
    }).join("");
    const body = regular + playoffHtml(divisionName ? [divisionName] : null);
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>IEFSC ${esc(title)}</title><style>
      *{font-family:Arial,Helvetica,sans-serif;color:#111;box-sizing:border-box}
      @page{size:letter;margin:0.5in}
      body{margin:0}
      .hdr{text-align:center;border-bottom:3px solid #FF5722;padding-bottom:12px;margin-bottom:16px}
      .hdr img{height:64px;object-fit:contain}
      .hdr h1{font-size:24px;margin:8px 0 2px;letter-spacing:1px}
      .hdr .sub{color:#555;font-size:13px}
      h2{background:#FF5722;color:#fff;padding:6px 10px;font-size:15px;margin:18px 0 8px;break-after:avoid}
      section.po h2{background:#111;border-left:8px solid #FF5722}
      h3{font-size:13px;margin:12px 0 4px;color:#FF5722;border-bottom:1px solid #ddd;padding-bottom:2px;break-after:avoid}
      table{width:100%;border-collapse:collapse;margin-bottom:8px}
      th,td{border:1px solid #ccc;padding:5px 8px;font-size:12px;text-align:left}
      th{background:#f2f2f2}
      thead{display:table-header-group}
      tr,section.wk h2,section.wk h3{break-inside:avoid}
      .vs{color:#FF5722;font-weight:bold;font-size:10px}
      .st{text-transform:uppercase;font-size:10px;white-space:nowrap}
      .code{font-weight:bold;color:#FF5722;white-space:nowrap}
      .seed{color:#777;font-size:10px}
      .tbd{color:#999;font-style:italic}
      .champ{font-weight:bold;font-size:13px;margin:4px 0 0;color:#111}
      .foot{margin-top:20px;text-align:center;color:#999;font-size:10px}
    </style></head><body>
      <div class="hdr"><img src="${logo}" alt="IEFSC" onerror="this.style.display='none'"/><h1>IEFSC — ${esc(title)}</h1><div class="sub">${esc(season)}</div></div>
      ${body || "<p style='text-align:center;color:#666'>No published games to display.</p>"}
      <div class="foot">Generated ${new Date().toLocaleString()} · iefscbasketball.com</div>
      <script>window.onload=function(){setTimeout(function(){window.print();},400);};<\/script>
    </body></html>`;
    const win = window.open("", "_blank");
    if (!win) return;
    win.document.open(); win.document.write(html); win.document.close();
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-16" data-testid="schedule-page">
      <SectionTitle overline="Game time" title="Schedule" sub="Every matchup of the season, week by week — past results and what's coming up next." />

      <div className="flex flex-wrap gap-2 mb-4 items-center">
        {DIVISIONS.map((d) => (
          <button key={d} data-testid={`schedule-division-${d.replace(/\s+/g, "-").toLowerCase()}`} onClick={() => { setDivision(d); setAllMode(false); setPlayoffMode(false); }}
            className={`px-5 py-2 text-sm font-bold uppercase tracking-wide transition-all duration-200 ${!allMode && !playoffMode && division === d ? "bg-[#FF5722] text-black" : "border border-white/15 text-white/70 hover:text-white"}`}>
            {d}
          </button>
        ))}
        <button data-testid="all-division-btn" onClick={() => { setAllMode(true); setPlayoffMode(false); }}
          className={`px-5 py-2 text-sm font-bold uppercase tracking-wide flex items-center gap-2 transition-all duration-200 ${allMode && !playoffMode ? "bg-[#FF5722] text-black" : "border border-[#FF5722]/60 text-[#FF5722] hover:bg-[#FF5722]/10"}`}>
          <ListBullets size={16} weight="bold" /> All Division Game Schedule
        </button>
        {playoffData && Object.values(playoffData.brackets || {}).some(Boolean) && (
          <button data-testid="playoff-schedule-btn" onClick={() => { setPlayoffMode(true); setAllMode(false); }}
            className={`px-5 py-2 text-sm font-bold uppercase tracking-wide flex items-center gap-2 transition-all duration-200 ${playoffMode ? "bg-[#FF5722] text-black" : "border border-[#FF5722]/60 text-[#FF5722] hover:bg-[#FF5722]/10"}`}>
            <Trophy size={16} weight="bold" /> Playoffs
          </button>
        )}
        {allMode && !playoffMode && admin && (
          <button data-testid="print-schedule-btn" onClick={() => printSchedule()}
            className="px-5 py-2 text-sm font-bold uppercase tracking-wide flex items-center gap-2 border border-white/25 text-white/80 hover:border-white hover:text-white transition-all duration-200">
            <Printer size={16} weight="bold" /> Print Schedule
          </button>
        )}
        {playoffMode && admin && (
          <button data-testid="print-playoffs-btn" onClick={() => printSchedule(null, true)}
            className="px-5 py-2 text-sm font-bold uppercase tracking-wide flex items-center gap-2 border border-white/25 text-white/80 hover:border-white hover:text-white transition-all duration-200">
            <Printer size={16} weight="bold" /> Print Playoffs
          </button>
        )}
        {!allMode && !playoffMode && admin && (
          <button data-testid="print-division-btn" onClick={() => printSchedule(division)}
            className="ml-auto px-5 py-2 text-sm font-bold uppercase tracking-wide flex items-center gap-2 border border-white/25 text-white/80 hover:border-white hover:text-white transition-all duration-200">
            <Printer size={16} weight="bold" /> Print {division}
          </button>
        )}
      </div>

      {!playoffMode && (
      <>
      <div className="flex flex-wrap gap-2 mb-2 items-center">
        {["all", "scheduled", "live", "completed"].map((f) => (
          <button key={f} data-testid={`filter-${f}`} onClick={() => setFilter(f)}
            className={`px-4 py-1.5 text-xs font-bold uppercase tracking-wide transition-all duration-200 ${filter === f ? "bg-white/15 text-white" : "border border-white/15 text-white/50 hover:text-white"}`}>
            {f === "all" ? "Active" : f === "scheduled" ? "Upcoming" : f === "completed" ? "Past / Final" : f}
          </button>
        ))}
        <select value={teamFilter} onChange={(e) => setTeamFilter(e.target.value)} data-testid="schedule-team-filter" className={`ml-auto bg-[#1A1A1A] border border-white/15 px-3 py-1.5 text-sm ${allMode ? "hidden" : ""}`}>
          <option value="all">All Teams</option>
          {divTeams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      </div>
      <p className="text-white/40 text-xs mb-8" data-testid="schedule-hint">
        {filter === "all"
          ? "Showing upcoming & active games — the current week is at the top. Completed games move to Past / Final automatically."
          : filter === "completed"
            ? "Past results — completed games are kept here for the season record."
            : `Filtered by ${filter}.`}
      </p>
      </>
      )}

      {playoffMode ? (
        <div className="space-y-12" data-testid="public-playoff-schedule">
          <p className="text-white/40 text-xs -mt-2">Every playoff game with its round, date, time and court — winners advance on the <a href="/playoffs" className="text-[#FF5722] underline">bracket</a> automatically.</p>
          {(playoffData?.divisions || []).map((d) => {
            const b = playoffData.brackets[d.key];
            if (!b) return null;
            return (
              <div key={d.key} data-testid={`playoff-schedule-div-${d.key.replace(/\s+/g, "-").toLowerCase()}`}>
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-2.5 h-7 bg-[#FF5722]" />
                  <h3 className="font-display text-3xl uppercase">{d.label} Division — Playoffs</h3>
                </div>
                <PlayoffScheduleTable bracket={b} />
              </div>
            );
          })}
        </div>
      ) : allMode ? (
        <div className="space-y-10" data-testid="all-division-schedule">
          {allGroups.length === 0 && <p className="text-white/40" data-testid="all-schedule-empty">{filter === "all" ? "No upcoming games across any division. Check Past / Final for completed results." : "No games match this filter."}</p>}
          {allGroups.map((grp) => (
            <div key={`all-w${grp.week}`} data-testid={`all-week-${grp.week}`}>
              <div className="flex items-center gap-3 mb-4">
                <span className="w-2.5 h-7 bg-[#FF5722]" />
                <h3 className="font-display text-3xl uppercase">Week {grp.week || ""}</h3>
                <span className="text-white/40 text-sm">{grp.games.length} games</span>
              </div>
              <div className="overflow-x-auto border border-white/10">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#161616] text-white/50 uppercase text-xs tracking-wide">
                      <th className="text-left px-3 py-2.5 font-semibold">Date</th>
                      <th className="text-left px-3 py-2.5 font-semibold">Time</th>
                      <th className="text-left px-3 py-2.5 font-semibold">Court</th>
                      <th className="text-left px-3 py-2.5 font-semibold">Division</th>
                      <th className="text-left px-3 py-2.5 font-semibold">Matchup</th>
                      <th className="text-left px-3 py-2.5 font-semibold">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {grp.games.map((g) => {
                      const st = STATUS[g.status] || STATUS.scheduled;
                      return (
                        <tr key={g.id} data-testid="all-schedule-row" className="border-t border-white/5 hover:bg-white/[0.03]">
                          <td className="px-3 py-2.5 whitespace-nowrap text-white/80">{fmtDate(g.date)}</td>
                          <td className="px-3 py-2.5 whitespace-nowrap font-bold text-[#F97316]">{fmtTime(g.date)}</td>
                          <td className="px-3 py-2.5 whitespace-nowrap text-white/70">{g.court || "—"}</td>
                          <td className="px-3 py-2.5 whitespace-nowrap"><span className="text-xs uppercase tracking-wide bg-white/10 text-white/70 px-2 py-0.5">{divisionOf(g.home_team_id)}</span></td>
                          <td className="px-3 py-2.5 font-semibold uppercase">
                            {teamName(g.home_team_id)} <span className="text-[#FF5722] mx-1">vs</span> {teamName(g.away_team_id)}
                            {g.status === "completed" && <span className="ml-2 text-white/50 font-normal normal-case">({g.home_score}-{g.away_score})</span>}
                          </td>
                          <td className="px-3 py-2.5 whitespace-nowrap">
                            <span className={`uppercase tracking-wide text-xs px-2 py-0.5 font-bold ${st.cls}`}>{st.label}</span>
                            {g.status === "completed" && (
                              <button onClick={() => setBoxGame(g.id)} data-testid={`view-boxscore-${g.id}`} className="ml-2 text-[#FF5722] hover:underline text-xs font-bold uppercase">Box</button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      ) : (
      <div className="space-y-10">
        {groups.length === 0 && (
          <div className="border-2 border-dashed border-white/15 py-16 text-center" data-testid="schedule-empty">
            <CalendarBlank size={40} className="text-white/25 mx-auto mb-3" />
            <p className="text-white/50">
              {filter === "all"
                ? "No upcoming games in this division right now. Check Past / Final for completed results."
                : "No games match this filter."}
            </p>
          </div>
        )}
        {groups.map((grp) => (
          <div key={grp.key} data-testid={`schedule-week-${grp.week ?? "all"}`}>
            {grp.week != null && (
              <div className="flex items-center gap-3 mb-4">
                <span className="w-2.5 h-7 bg-[#FF5722]" />
                <h3 className="font-display text-3xl uppercase">Week {grp.week}</h3>
                {grp.meta?.start_date && <span className="text-white/40 text-sm">{grp.meta.start_date}{grp.meta.end_date && grp.meta.end_date !== grp.meta.start_date ? ` – ${grp.meta.end_date}` : ""}</span>}
              </div>
            )}
            <div className="space-y-4">
              {grp.games.map((g) => {
                const st = STATUS[g.status] || STATUS.scheduled;
                const faded = g.status === "cancelled";
                return (
                  <div key={g.id} data-testid="schedule-game-card" className={`border border-white/10 bg-[#121212] p-6 ${faded ? "opacity-60" : ""}`}>
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex-1 flex items-center justify-end gap-3">
                        <div className="font-display text-3xl sm:text-4xl lg:text-5xl uppercase text-right">{teamName(g.home_team_id)}</div>
                        <Logo team={teamOf(g.home_team_id)} />
                      </div>
                      <div className="text-center px-4 min-w-[110px]">
                        {g.status === "completed" ? (
                          <div className="font-display text-4xl sm:text-5xl tabular-nums">
                            <span className={g.home_score >= g.away_score ? "text-[#FF5722]" : "text-white/60"}>{g.home_score}</span>
                            <span className="text-white/30 mx-2">-</span>
                            <span className={g.away_score >= g.home_score ? "text-[#FF5722]" : "text-white/60"}>{g.away_score}</span>
                          </div>
                        ) : (
                          <div className="font-display text-3xl sm:text-4xl text-[#FF5722]">VS</div>
                        )}
                      </div>
                      <div className="flex-1 flex items-center gap-3">
                        <Logo team={teamOf(g.away_team_id)} />
                        <div className="font-display text-3xl sm:text-4xl lg:text-5xl uppercase">{teamName(g.away_team_id)}</div>
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-white/5 flex flex-col items-center gap-3">
                      <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 font-bold text-[#F97316] text-lg sm:text-xl lg:text-2xl" data-testid="schedule-game-meta">
                        <span className="flex items-center gap-1.5"><CalendarBlank size={24} weight="bold" /> {new Date(g.date).toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" })}</span>
                        <span className="flex items-center gap-1.5"><Clock size={24} weight="bold" /> {new Date(g.date).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                        {g.court && <span className="flex items-center gap-1.5"><Basketball size={24} weight="fill" /> {g.court}</span>}
                      </div>
                      <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-white/50">
                        {g.venue && <span className="flex items-center gap-1"><MapPin size={16} /> {g.venue}</span>}
                        <span className={`uppercase tracking-wide text-xs px-2 py-0.5 font-bold ${st.cls}`}>{st.label}</span>
                        {g.status === "completed" && (
                          <button onClick={() => setBoxGame(g.id)} data-testid={`view-boxscore-${g.id}`} className="flex items-center gap-1.5 border border-[#FF5722]/50 text-[#FF5722] hover:bg-[#FF5722]/10 uppercase tracking-wide text-xs font-bold px-3 py-1 transition-all duration-200">
                            <ClipboardText size={14} weight="bold" /> Box Score
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      )}

      {boxGame && <BoxScoreModal gameId={boxGame} onClose={() => setBoxGame(null)} />}
    </div>
  );
}
